import {
  pathOr,
  flatten,
  pluck,
  propEq,
  find,
  pipe,
  anyPass,
  last,
  reject
} from 'ramda'

import {roomsFetchDone, roomsFetchError} from './slice'

import {OVERLAY_IDS} from 'modules/overlay/selectors'

import {
  ActionLink,
  ActionLinkTypes
} from '@daedalus/core/src/offer/types/actionLink'
import {Offer, OfferPrice} from '@daedalus/core/src/offer/types/offer'

import {
  isMemberPlusDeal,
  isPrivateDeal
} from '@daedalus/core/src/offer/business/privateDeals'
import {Room} from '@daedalus/core/src/room/types/room'
import {TaxDisplayLogic} from '@daedalus/core/src/offer/types/TaxDisplayLogic'
import {isCugHidden} from '@daedalus/core/src/accessControl/modules/selectors'

import {StoreStateType} from 'modules'
import {createSelector} from '@reduxjs/toolkit'

export type GetFormInfoType = {
  offer: Offer
  room: Room
  roomPosition: number
  offerPosition: number
  feesMnd: string
}

const applyRateControl = (offers: Offer[]) =>
  reject((offer: Offer) => Boolean(offer.cug?.length), offers)

const filterCugOffersFromRoom = (room: Room) => ({
  ...room,
  offers: applyRateControl(room.offers)
})

const filterNoOfferRooms = (room: Room) => room.offers.length

const applyRateControlToRoom = (rooms: Room[]) =>
  rooms.map(filterCugOffersFromRoom).filter(filterNoOfferRooms)

const pathToRooms = ['rooms', 'roomsList']

export const getRooms = (state: StoreStateType) => {
  const rooms = pathOr([], pathToRooms)(state)

  if (!isCugHidden(state)) return rooms
  return applyRateControlToRoom(rooms)
}

// TODO: @lukKowalski remove after f9191b40-room-selection-performance-improvements is ended
export const getRoomsB = createSelector(
  pathOr([], pathToRooms),
  isCugHidden,
  (rooms, isCugHidden) => {
    if (!isCugHidden) return rooms
    return applyRateControlToRoom(rooms)
  }
)

export const getCheapestRoom = (state: StoreStateType) =>
  pathOr(null, ['0'], getRooms(state))

// TODO: @lukKowalski remove after f9191b40-room-selection-performance-improvements is ended
export const getCheapestRoomB = (state: StoreStateType) =>
  pathOr(null, ['0'], getRoomsB(state))

export const getCheapestOffer = (state: StoreStateType) =>
  pathOr(null, ['0', 'offers', '0'], getRooms(state))

// TODO: @lukKowalski remove after f9191b40-room-selection-performance-improvements is ended
export const getCheapestOfferB = (state: StoreStateType) =>
  pathOr(null, ['0', 'offers', '0'], getRoomsB(state))

export const getSearchSelectedRoomIndex = (state: StoreStateType): number => {
  return getRooms(state).findIndex(room => room.hasClickedOffer)
}

export const getSearchSelectedRoom = (state: StoreStateType): Room => {
  const selectedRoomIndex = getSearchSelectedRoomIndex(state)
  return getRooms(state)[selectedRoomIndex] || null
}

export const getSearchSelectedOffer = (state: StoreStateType): Offer => {
  const searchSelectedRoom = getSearchSelectedRoom(state)

  if (!searchSelectedRoom) {
    return null
  }

  return searchSelectedRoom.offers.find(offer => offer.isClicked)
}

export const nightlyDisplayTotalPath = ['chargeable', 'nightlyDisplayTotal']

export const basePath = ['chargeable', 'base']
export const nightlyBasePath = ['chargeable', 'nightlyBase']

export const taxesPath = ['chargeable', 'taxes']
export const nightlyTaxesPath = ['chargeable', 'nightlyTaxes']

/* TODO: remove total paths after BoFH remove these properties from the api */
export const nightlyTotalPath = ['chargeable', 'nightlyTotal']
export const totalPath = ['chargeable', 'total']

export const hotelFeesPath = ['hotelFees', 'total']

export const getHotelFees = pathOr(0, hotelFeesPath)

export const getHotelNightlyFees = (price: OfferPrice) =>
  price?.hotelFees?.nightly ?? 0

export const hotelFeesNightlyPath = ['hotelFees', 'nightly']

export const hotelFeesBreakdownPath = ['hotelFees', 'breakdown']

export const getHotelFeesBreakdown = pathOr([], hotelFeesBreakdownPath)

export const getRoomById = (roomId: string) => (state: StoreStateType) => {
  return pipe(getRooms, find<Room>(propEq('id', roomId)))(state)
}

// TODO: @lukKowalski remove after f9191b40-room-selection-performance-improvements is ended
export const getRoomByIdB = (roomId: string) => (state: StoreStateType) => {
  return pipe(getRoomsB, find<Room>(propEq('id', roomId)))(state)
}

export const getModuleState = (state: StoreStateType) =>
  state?.rooms?.fetchRoomsState

export const getOfferById =
  (roomId: string, offerId: string) => (state: StoreStateType) => {
    const room = getRoomById(roomId)(state)
    if (!room) return null

    const offers = pathOr(null, ['offers'], room)
    return find<Offer>(propEq('id', offerId))(offers)
  }

export const getRoomDetails = (state: StoreStateType) => {
  const topmostOverlay = last(state.overlay)
  if (
    !topmostOverlay ||
    !propEq('id', OVERLAY_IDS.roomDetails, topmostOverlay)
  ) {
    return
  }

  const overlayState = topmostOverlay.extraState

  return getRoomById(overlayState.roomId)(state)
}

export const getSelectedRoomPosition = (state: StoreStateType) => {
  return state.rooms?.selectedRoomPosition
}

export const getSelectedOfferPosition = (state: StoreStateType) => {
  return state.rooms?.selectedOfferPosition
}

export const getSelectedOfferCheckoutUrl = (
  state: StoreStateType,
  roomId: string,
  offerId: string
) => {
  const storedUrl = state?.rooms?.selectedOfferCheckoutUrl
  if (storedUrl) return storedUrl

  const offer = getOfferById(roomId, offerId)(state)
  const checkoutLink = find<ActionLink>(
    propEq('type', ActionLinkTypes.CHECKOUT),
    offer?.links || []
  )
  return checkoutLink?.href
}

export const getFormInfo = (
  state: StoreStateType,
  roomId: string,
  offerId: string
): GetFormInfoType => {
  const room = getRoomById(roomId)(state)
  const offer = getOfferById(roomId, offerId)(state)
  const roomPosition = getSelectedRoomPosition(state)
  const offerPosition = getSelectedOfferPosition(state)

  if (offer && room) {
    return {
      feesMnd: state?.hotelApi?.data?.feesMnd,
      offer,
      room,
      roomPosition,
      offerPosition
    }
  }
}

export const getAreRoomsLoaded = (state: StoreStateType) => {
  return anyPass([
    propEq('fetchRoomsState', roomsFetchDone.type),
    propEq('fetchRoomsState', roomsFetchError.type)
  ])(state.rooms)
}

export const getTaxDisplayLogic = createSelector(
  state => state.rooms,
  (state: StoreStateType['rooms']): TaxDisplayLogic => {
    return state?.taxDisplayLogic
  }
)

export const getFirstPrivateDealAvailable = (state: StoreStateType): Offer => {
  return pipe(getRooms, pluck('offers'), flatten, find(isPrivateDeal))(state)
}

export const getFirstMemberPlusDealAvailable = (
  state: StoreStateType
): Offer => {
  return pipe(getRooms, pluck('offers'), flatten, find(isMemberPlusDeal))(state)
}

export const hasPrivateDealAvailable = (state: StoreStateType) => {
  return Boolean(getFirstPrivateDealAvailable(state))
}

export const hasMemberPlusDealAvailable = (state: StoreStateType) =>
  Boolean(getFirstMemberPlusDealAvailable(state))
